/*
 * aura-product.js: product page behavior (spec 07).
 * <aura-media-tabs>       Photos / 3D / Your car tablist (arrow keys, roving tabindex). Your car
 *                         holds the AI try-on (aura-tryon.js), which runs in the panel. On phones
 *                         the tablist sticks over the media and is marked .is-stuck once it has moved
 * <aura-gallery>          thumbnails (a column on desktop, a row under the photo on phones): a click
 *                         scrolls the stage to that photo (the links jump there without JavaScript);
 *                         the thumbnail on screen is marked current and kept in view
 * <aura-buy-box>          length tiles update price, button label, URL ?variant= and the
 *                         Shop Pay installments banner; the mobile buy bar shows whenever the
 *                         main Add button is off screen (from arrival). It carries a length (or option)
 *                         switcher that drives the tiles, the discount button (aura-meerkat.js) and Add.
 *                         Without a switcher, until a length is picked or the tiles have been on screen
 *                         it offers "Choose length", which scrolls to the tiles; then "Add 58 ft". It
 *                         hides while a buy-box field has focus. For wholesale
 *                         customers the tiles carry data-regular, and the struck regular price
 *                         and "Wholesale" label (snippets/price.liquid) follow the picked length;
 *                         the same for a discount-unlocked visitor ("Discount unlocked")
 * <aura-recommendations>  "Nearby colors" fallback from Shopify product recommendations
 * <aura-recommended-colors>  "Recommended for you": the colors closest in color and finish to the ones
 *                         this shopper has looked at (aura-taste.js), rendered as color cards by
 *                         sections/recommended-colors.liquid on /search?q=<handles>
 */
(() => {
  const aura = window.aura;
  const track = (n, p) => aura && aura.track(n, p);

  class AuraMediaTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.querySelectorAll('[data-js-hidden]').forEach((p) => { p.hidden = true; p.removeAttribute('data-js-hidden'); });
      if (!this.tabs.length) return;
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.select(tab);
          /* Your car readies the try-on in its panel (aura-tryon.js), on a click only, not as arrow keys pass it */
          if (tab.dataset.tab === 'your_car') this.openTryon(tab, 'pdp_tab');
        });
        tab.addEventListener('keydown', (e) => {
          const i = this.tabs.indexOf(tab);
          let next = null;
          if (e.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
          if (e.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
          if (e.key === 'Home') next = this.tabs[0];
          if (e.key === 'End') next = this.tabs[this.tabs.length - 1];
          if (next) { e.preventDefault(); this.select(next); next.focus(); }
        });
      });
      /* Phones: the tablist sticks under the header (section-product.css) and slims down once it has left its place */
      const list = this.querySelector('.media-tabs__list');
      let queued = false;
      const stuck = () => { queued = false; list.classList.toggle('is-stuck', list.getBoundingClientRect().top - this.getBoundingClientRect().top > 1); };
      window.addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(stuck); } }, { passive: true });
      stuck();
      const requested = new URLSearchParams(location.search).get('tab');
      const start = this.tabs.find((t) => t.dataset.tab === requested);
      if (start) this.select(start, true);
      /* ?tab=your_car (the "On my car" links on cards and the home hero) readies the try-on as well */
      if (start && start.dataset.tab === 'your_car') this.openTryon(start, 'link');
    }
    openTryon(opener, surface) {
      customElements.whenDefined('aura-tryon').then(() => {
        const tryon = this.closest('.product')?.querySelector('aura-tryon');
        if (tryon) tryon.open(opener, surface);
      });
    }
    select(tab, silent) {
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
      });
      if (!silent) {
        const url = new URL(location.href);
        if (tab.dataset.tab === 'photos') url.searchParams.delete('tab'); else url.searchParams.set('tab', tab.dataset.tab);
        history.replaceState(history.state, '', url);
        track('aura_media_tab_changed', { to: tab.dataset.tab });
      }
    }
  }

  class AuraGallery extends HTMLElement {
    connectedCallback() {
      const stage = this.querySelector('.gallery');
      this.rail = this.querySelector('.gallery__thumbs');
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      if (!stage || !this.thumbs.length) return;
      const slides = [...stage.children];
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', (e) => {
        e.preventDefault();
        if (!slides[i]) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        stage.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      }));
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) this.mark(slides.indexOf(entry.target)); });
      }, { root: stage, threshold: 0.6 });
      slides.forEach((slide) => io.observe(slide));
    }

    mark(index) {
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
      });
      /* More thumbnails than fit: keep the current one in the middle of the rail (down the column, across the row) */
      const thumb = this.thumbs[index];
      const { rail } = this;
      if (thumb && (rail.scrollHeight > rail.clientHeight || rail.scrollWidth > rail.clientWidth)) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        rail.scrollTo({
          top: thumb.offsetTop - (rail.clientHeight - thumb.offsetHeight) / 2,
          left: thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2,
          behavior: reduce ? 'auto' : 'smooth',
        });
      }
    }
  }

  class AuraBuyBox extends HTMLElement {
    connectedCallback() {
      const section = this.closest('.product');
      this.data = JSON.parse(section.querySelector('[data-product-json]').textContent);
      this.form = this.querySelector('form');
      this.addBtn = this.querySelector('[data-add-button]');
      this.label = this.querySelector('[data-add-label]');
      this.price = this.querySelector('[data-add-price]');
      this.priceWrap = this.querySelector('[data-price-wrap]');
      this.bar = section.querySelector('[data-buy-bar]');
      this.barVariant = section.querySelector('[data-bar-variant]');
      this.barSize = section.querySelector('[data-bar-size]');

      /* Length tiles: keep radios named "id" for no-JS; with JS one hidden input carries the id
         so Shopify's installments banner sees a single variant field change. */
      const radios = [...this.querySelectorAll('[data-length-cards] input[type="radio"]')];
      if (radios.length) {
        this.idInput = document.createElement('input');
        this.idInput.type = 'hidden';
        this.idInput.name = 'id';
        this.idInput.value = (radios.find((r) => r.checked) || radios[0]).value;
        this.form.prepend(this.idInput);
        radios.forEach((r) => {
          r.name = `length-${this.dataset.sectionId}`;
          r.addEventListener('change', () => this.onVariant(r));
        });
      }
      this.querySelector('[data-variant-select]')?.addEventListener('change', (e) => {
        const opt = e.target.selectedOptions[0];
        this.setPrice(opt.dataset.price, opt.dataset.regular);
        this.syncUrl(e.target.value);
        this.chosen = true;
        this.barLabel = opt.textContent.trim();
        this.updateBar();
      });

      /* Mobile buy bar. Its switcher has the same choices as the tiles or the select, as a native select (a wheel
         on phones). With the length in the bar there is nothing to choose first: Add shows from the start */
      this.barBtn = this.bar && this.bar.querySelector('[data-bar-button]');
      this.chosen = !!this.barSize || !this.data.bar_choose || !!this.data.variant_param;
      if (this.barSize) {
        const select = this.querySelector('[data-variant-select]');
        const choices = radios.length ? radios : [...(select ? select.options : [])];
        this.barSize.replaceChildren(...choices.map((c) => {
          const text = radios.length ? `${c.dataset.label} / ${c.dataset.price}${c.disabled ? ` (${this.data.sold_out})` : ''}` : c.textContent.trim();
          const option = new Option(text, c.value);
          option.disabled = c.disabled;
          return option;
        }));
        this.barSize.addEventListener('change', () => {
          const radio = radios.find((r) => r.value === this.barSize.value);
          if (radio) { radio.checked = true; this.onVariant(radio); return; }
          if (!select) return;
          select.value = this.barSize.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      this.addVisible = true;
      this.typing = false;
      if (this.bar && this.addBtn && this.barBtn && 'IntersectionObserver' in window) {
        /* "On screen" means in the free area: not under the sticky header, not under the bar itself */
        const css = getComputedStyle(document.documentElement);
        const top = parseInt(css.getPropertyValue('--header-h'), 10) || 0;
        const bottom = parseInt(css.getPropertyValue('--buy-bar-h'), 10) || 0;
        new IntersectionObserver(([entry]) => {
          this.addVisible = entry.isIntersecting;
          /* Add sits below the tiles: once it is above the free area, the shopper has scrolled past them */
          if (!entry.isIntersecting && entry.rootBounds && entry.boundingClientRect.bottom <= entry.rootBounds.top) this.chosen = true;
          this.updateBar();
        }, { rootMargin: `-${top}px 0px -${bottom}px 0px`, threshold: 1 }).observe(this.addBtn);
        const choices = this.querySelector('[data-length-cards]') || this.querySelector('[data-variant-select]');
        if (choices && !this.chosen) {
          const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { this.chosen = true; io.disconnect(); this.updateBar(); } }, { threshold: 0.6 });
          io.observe(choices);
        }
        this.barBtn.addEventListener('click', (e) => {
          if (this.chosen) return;
          e.preventDefault();
          this.chooseLength();
        });
        const field = (el) => el && el.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select');
        this.addEventListener('focusin', (e) => { if (field(e.target)) { this.typing = true; this.updateBar(); } });
        this.addEventListener('focusout', (e) => { if (field(e.target)) { this.typing = false; this.updateBar(); } });
        this.updateBar();
      }

      /* Details: the panel slides up over the buy box; Escape or a click elsewhere closes it */
      const details = this.querySelector('[data-details]');
      if (details) {
        const btn = details.querySelector('[data-details-toggle]');
        const panel = details.querySelector('.buy-box__details-panel');
        const setOpen = (open) => {
          details.classList.toggle('is-open', open);
          btn.setAttribute('aria-expanded', open);
          panel.inert = !open;
        };
        btn.addEventListener('click', () => setOpen(!details.classList.contains('is-open')));
        document.addEventListener('click', (e) => { if (!details.contains(e.target)) setOpen(false); });
        details.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setOpen(false); btn.focus(); } });
      }

      track('aura_pdp_viewed', { handle: this.data.handle, line: this.data.line, sheen: this.data.sheen, build_count: this.data.build_count, has_garage_car: false });
    }

    updateBar() {
      if (!this.barBtn) return;
      const show = !this.addVisible && !this.typing;
      this.bar.classList.toggle('is-visible', show);
      this.bar.classList.toggle('is-cta-visible', this.addVisible);
      this.bar.setAttribute('aria-hidden', String(!show));
      this.bar.inert = !show;
      this.barBtn.tabIndex = show ? 0 : -1;
      const checked = this.querySelector('[data-length-cards] input:checked');
      const len = this.form.dataset.lengthFt;
      if (!this.chosen) {
        this.setBar(this.data.bar_choose);
        if (this.barVariant) this.barVariant.textContent = this.data.bar_from;
        return;
      }
      if (this.barSize) this.barSize.value = this.idInput ? this.idInput.value : this.form.querySelector('[data-variant-select]')?.value || this.barSize.value;
      if (this.addBtn.disabled) return;
      this.setBar(len && !this.barSize ? this.data.bar_add_length.replace('[length]', len) : this.data.bar_add);
      if (checked) this.setBarVariant(`${len ? `${len} ft` : checked.dataset.label} / ${checked.dataset.price}`);
      else if (this.barLabel) this.setBarVariant(this.barLabel);
    }

    /* "58 ft / $530". In the switcher a long name ("58x5 (Full roll)") gives way before the price does */
    setBarVariant(text) {
      if (!this.barVariant) return;
      const at = text.lastIndexOf(' / ');
      if (!this.barSize || at < 0) { this.barVariant.textContent = text; return; }
      const name = document.createElement('span');
      const price = document.createElement('span');
      name.textContent = text.slice(0, at);
      price.textContent = text.slice(at);
      this.barVariant.replaceChildren(name, price);
    }

    /* The bar button's visible label plus the product name for screen readers ("Add 58 ft, Viper Green"),
       so it isn't one more anonymous "Add" on the page (WCAG 2.4.6) */
    setBar(label) {
      this.barBtn.textContent = label;
      if (!this.data.name) return;
      const name = document.createElement('span');
      name.className = 'visually-hidden';
      name.textContent = `, ${this.data.name}`;
      this.barBtn.append(name);
    }

    chooseLength() {
      const target = this.querySelector('[data-length-cards]') || this.querySelector('[data-variant-select]');
      this.chosen = true;
      if (target) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
        const focusEl = target.matches('select') ? target : (target.querySelector('input:checked') || target.querySelector('input:not([disabled])'));
        if (focusEl) focusEl.focus({ preventScroll: true });
      }
      this.updateBar();
    }

    onVariant(radio) {
      this.idInput.value = radio.value;
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      const len = radio.dataset.lengthFt;
      this.form.dataset.lengthFt = len || '';
      this.label.textContent = len ? this.data.add_label.replace('[length]', len) : this.data.add_label_plain;
      this.setPrice(radio.dataset.price, radio.dataset.regular);
      this.chosen = true;
      this.updateBar();
      this.syncUrl(radio.value);
      track('aura_length_selected', { length_ft: len ? Number(len) : null, recommended: null, used_recommended: false });
    }

    setPrice(formatted, regular) {
      if (this.price) this.price.textContent = `/ ${formatted}`;
      if (!this.priceWrap) return;
      const money = this.priceWrap.querySelector('[data-money]');
      if (!money) {
        const amount = this.priceWrap.querySelector('.price');
        if (amount) amount.textContent = formatted;
        return;
      }
      /* Wholesale or discount unlocked: swap the amount and the struck regular price; keep the hidden labels.
         "Discount unlocked" hides with the struck price on a length that gets no markdown */
      money.textContent = formatted;
      const was = this.priceWrap.querySelector('[data-money-was]');
      if (was && regular) {
        was.textContent = regular;
        was.closest('s').hidden = regular === formatted;
        const label = this.priceWrap.querySelector('[data-money-label]');
        if (label) label.hidden = regular === formatted;
      }
    }

    syncUrl(variantId) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variantId);
      history.replaceState(history.state, '', url);
    }
  }

  class AuraRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = async () => {
        try {
          const html = await (await fetch(this.dataset.url)).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('aura-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          else this.closest('.section')?.remove();
        } catch (e) { /* leave the section empty */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else load();
    }
  }

  const CANDIDATES = 20; /* all_products allows 20 handles; sold-out ones drop out in the section */
  const SKIP_SEEN = 10; /* the colors looked at most lately are left out */

  class AuraRecommendedColors extends HTMLElement {
    connectedCallback() {
      if (this.observer || !aura || !aura.taste) return;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 600px 0px' });
      /* hidden means no box to intersect, so watch the section wrapper instead */
      this.observer.observe(this.parentElement || this);
    }

    disconnectedCallback() { this.observer?.disconnect(); }

    async load() {
      try {
        const handles = this.rank(await aura.taste.table());
        if (!handles.length) return;
        const res = await fetch(`${this.dataset.url}&q=${encodeURIComponent(handles.join(' '))}`);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const cards = [...doc.querySelectorAll('[data-recommended-cards] > li')].slice(0, Number(this.dataset.limit) || 12);
        if (!cards.length) return;
        this.querySelector('[data-recommended-rail]').replaceChildren(...cards);
        this.hidden = false;
      } catch (e) { /* the section stays hidden */ }
    }

    /* aura.taste.look(): how close each color comes, in color and finish, to the colors this shopper has looked
       at (this one counting double, as the newest). Only this line (vinyl or PPF), one finish per color, and
       none of this color, the colors looked at most lately or the More like this cards */
    rank(table) {
      const self = table.get(this.dataset.handle);
      if (!self) return [];
      const taste = aura.taste;
      const seen = taste.views(table);
      const skip = new Set([...taste.products()].sort((a, b) => b[1] - a[1]).slice(0, SKIP_SEEN).map(([h]) => h).concat(this.dataset.handle));
      document.querySelectorAll('.nearby .card[data-handle]').forEach((c) => skip.add(c.dataset.handle));
      const bases = new Set([self.base]);
      /* a shuffle first gives equal colors a turn on each visit */
      const pool = [...table].filter(([h, r]) => r.ppf === self.ppf && !skip.has(h));
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      const out = [];
      for (const { h, r } of pool.map(([h, r]) => ({ h, r, s: taste.look(r, seen) || 0 })).sort((a, b) => b.s - a.s)) {
        if (bases.has(r.base)) continue;
        bases.add(r.base);
        out.push(h);
        if (out.length === CANDIDATES) break;
      }
      return out;
    }
  }

  [['aura-media-tabs', AuraMediaTabs], ['aura-gallery', AuraGallery], ['aura-buy-box', AuraBuyBox], ['aura-recommendations', AuraRecommendations], ['aura-recommended-colors', AuraRecommendedColors]]
    .forEach(([name, cls]) => { if (!customElements.get(name)) customElements.define(name, cls); });
})();
